/**
 * FairwayBid – die Dealchance
 *
 * Ein Anbieter sieht zwanzig offene Anfragen und hat Zeit für drei Angebote.
 * Die Frage ist nicht, welche Anfrage die größte ist, sondern bei welcher er
 * überhaupt eine Chance hat. Diese Antwort steckt bereits in den Daten: Er
 * hat bei der Registrierung gesagt, welche Kategorien er führt, wohin er
 * liefert und welche Leistungen er erbringt – und die Anfrage sagt, was sie
 * braucht. Beides nebeneinanderzuhalten war bisher Handarbeit.
 *
 * Was die Zahl ist – und was nicht
 * --------------------------------
 * Sie ist ein Abgleich, keine Vorhersage. Wir wissen nicht, wie viele
 * Anbieter mitbieten, was sie kalkulieren und wen die Anlage kennt. Wer daraus
 * eine Wahrscheinlichkeit macht, erfindet eine Genauigkeit, die es nicht gibt
 * – und ein Anbieter, der einmal gemerkt hat, dass "80 %" nichts bedeutet,
 * schaut nie wieder hin.
 *
 * Deshalb zeigt die Anzeige, woraus sich die Zahl ergibt: jeder Punkt eine
 * Zeile, jede Zeile eine Angabe aus dem Profil gegen eine Angabe der Anfrage.
 * Damit ist sie nachprüfbar und, wo sie niedrig ausfällt, umsetzbar –
 * "Niederlande nicht in Ihrem Liefergebiet" ist ein Satz, auf den man etwas
 * tun kann.
 *
 * Die Gewichte
 * ------------
 *   Kategorie    40  Die Grundbedingung. Wer Mähroboter nicht führt, gibt
 *                    auf eine Mähroboter-Anfrage kein Angebot ab. Passt sie
 *                    nicht, ist die Chance gering, egal wie gut der Rest steht.
 *   Lieferland   25  Fracht und Service hängen daran. Unbekannt zählt halb:
 *                    Ein leeres Profilfeld ist kein Nein.
 *   Leistungen   20  Montage, Wartung, Vor-Ort-Service. Anteilig danach, wie
 *                    viel von dem Gewünschten das Profil abdeckt.
 *   Klarheit     15  Stückzahl, Budget und Termin in der Anfrage. Nicht die
 *                    Schuld des Anbieters, aber sein Risiko: Eine Anfrage
 *                    ohne Zahlen kostet Rückfragen und wird oft nicht vergeben.
 *
 * Die Frist geht nicht in die Punkte ein, sondern dämpft das Ergebnis: Was
 * morgen abläuft, ist keine bessere Gelegenheit, nur eine eiligere.
 */

import { getDeadlineStatus } from './deadlines';
import { getDealerCategories, getDealerRegions, CATEGORY_LABELS } from './matching';
import { getServices, SERVICE_LABELS, COUNTRY_LABELS } from './coverage';
import { getCategory, getQuantity, isBundleOpen, istBuendelfaehig } from './bundles';

export const GEWICHT = { kategorie: 40, land: 25, leistungen: 20, klarheit: 15 };

/** Ab wann die Anzeige welche Stufe zeigt. */
export const STUFEN = [
  { ab: 70, name: 'hoch', text: 'Passt gut zu Ihrem Profil' },
  { ab: 45, name: 'mittel', text: 'Passt teilweise' },
  { ab: 0, name: 'gering', text: 'Passt wenig' },
];

export const stufeFuer = wert => STUFEN.find(s => wert >= s.ab) || STUFEN[STUFEN.length - 1];

const alsListe = wert => (Array.isArray(wert) ? wert.filter(Boolean) : wert ? [wert] : []);

/**
 * Der Abgleich zwischen einem Händlerprofil und einer Anfrage.
 *
 * @param {Object} user Händler (CurrentUser-Entity)
 * @param {Object} listing die Anfrage
 * @param {Date} [now]
 * @returns {Object|null} null, solange das Profil keine Kategorien nennt –
 *          dann ist jeder Wert geraten, und die Anzeige bittet stattdessen
 *          darum, das Profil zu ergänzen.
 */
export const dealChance = (user, listing, now = new Date()) => {
  const kategorien = getDealerCategories(user);
  if (kategorien.length === 0 || !listing) return null;

  const pd = listing?.attributes?.publicData || {};
  const gruende = [];
  let punkte = 0;

  // --- Kategorie ---
  const kategorie = getCategory(listing);
  const kategorieName = CATEGORY_LABELS[kategorie] || 'diese Kategorie';
  const kategoriePasst = !!kategorie && kategorien.includes(kategorie);
  if (kategoriePasst) {
    punkte += GEWICHT.kategorie;
    gruende.push({ gut: true, text: `${kategorieName} gehört zu Ihrem Sortiment` });
  } else {
    gruende.push({ gut: false, text: `${kategorieName} ist nicht in Ihrem Sortiment hinterlegt` });
  }

  // --- Lieferland ---
  const land = pd.delivery_country || null;
  const regionen = getDealerRegions(user);
  const landName = COUNTRY_LABELS[land] || land;
  if (!land || regionen.length === 0) {
    punkte += GEWICHT.land / 2;
    gruende.push({
      gut: null,
      text: 'Lieferland nicht abgeglichen – Angabe fehlt auf einer Seite',
    });
  } else if (regionen.includes(land)) {
    punkte += GEWICHT.land;
    gruende.push({ gut: true, text: `Sie liefern nach ${landName}` });
  } else {
    gruende.push({ gut: false, text: `${landName} ist nicht in Ihrem Liefergebiet` });
  }

  // --- Leistungen ---
  const gewuenscht = alsListe(pd.requested_services);
  const eigene = getServices(user);
  if (gewuenscht.length === 0) {
    punkte += GEWICHT.leistungen;
    gruende.push({ gut: true, text: 'Keine besonderen Leistungen gefordert' });
  } else {
    const abgedeckt = gewuenscht.filter(s => eigene.includes(s));
    const fehlend = gewuenscht.filter(s => !eigene.includes(s));
    punkte += Math.round((GEWICHT.leistungen * abgedeckt.length) / gewuenscht.length);
    if (fehlend.length === 0) {
      gruende.push({ gut: true, text: 'Alle gewünschten Leistungen bieten Sie an' });
    } else {
      const namen = fehlend.map(s => SERVICE_LABELS[s] || s).join(', ');
      gruende.push({
        gut: false,
        text: `Nicht in Ihrem Profil: ${namen}`,
      });
    }
  }

  // --- Klarheit der Anfrage ---
  const klar = [
    { da: getQuantity(listing) !== null, name: 'Stückzahl' },
    { da: !!pd.budget_range, name: 'Budgetrahmen' },
    { da: !!pd.delivery_date, name: 'Wunschtermin' },
  ];
  const vorhanden = klar.filter(k => k.da);
  punkte += Math.round((GEWICHT.klarheit * vorhanden.length) / klar.length);
  const fehlt = klar.filter(k => !k.da).map(k => k.name);
  if (fehlt.length === 0) {
    gruende.push({ gut: true, text: 'Stückzahl, Budget und Termin stehen in der Anfrage' });
  } else {
    gruende.push({ gut: null, text: `Die Anfrage nennt nicht: ${fehlt.join(', ')}` });
  }

  // --- Bündel: kein Punkt, aber eine Auskunft ---
  if (isBundleOpen(listing) && istBuendelfaehig(listing)) {
    gruende.push({ gut: true, text: 'Für Bündelung freigegeben – die Menge kann noch wachsen' });
  }

  // --- Frist als Dämpfer ---
  const frist = getDeadlineStatus(listing, now);
  let wert = punkte;
  if (frist?.expired) {
    wert = 0;
    gruende.push({ gut: false, text: 'Die Angebotsfrist ist abgelaufen' });
  } else if (frist && frist.daysLeft <= 2) {
    wert = Math.round(wert * 0.8);
    gruende.push({ gut: null, text: 'Wenig Zeit – die Frist läuft in Kürze ab' });
  }

  // Passt die Kategorie nicht, taugt die Summe nicht als Aussage: Ein Anbieter
  // ohne das Produkt hat keine halbe Chance, sondern fast keine.
  if (!kategoriePasst) wert = Math.min(wert, 15);

  wert = Math.max(0, Math.min(100, Math.round(wert)));

  return { wert, stufe: stufeFuer(wert), gruende, kategoriePasst };
};
